import {
    all,
    take,
    takeEvery,
    takeLatest,
    select,
    apply,
    put,
    call,
    delay
} from 'redux-saga/effects';

import {
    FETCH_ACCOUNT_SUCCEEDED,

    FILTER_ATHLETES,

    ATTEMPT_FETCH_ATHLETES,
    FETCH_ATHLETES_SUCCEEDED,

    ATTEMPT_FETCH_ATHLETE,
    FETCH_ATHLETE_SUCCEEDED,

    EDIT_ATHLETE,
    LOADED_CACHED_ATHLETE,

    ATTEMPT_CREATE_ATHLETE,
    CREATE_ATHLETE_SUCCEEDED,
    CREATE_ATHLETE_FAILED,

    ATTEMPT_SAVE_ATHLETE_METADATA,
    SAVE_ATHLETE_METADATA_SUCCEEDED,

    UPDATE_ATHLETE_CATEGORY_ATTEMPT,
    UPDATE_ATHLETE_CATEGORY_SUCCEEDED,

    ATTEMPT_UPLOAD_AVATAR,
    UPLOAD_AVATAR_SUCCEEDED,
    UPLOAD_AVATAR_FAILED,

    ATTEMPT_IMPORT_ATHLETES,
    IMPORT_ATHLETES_SUCCEEDED,
    IMPORT_ATHLETES_MISSING_COLUMNS,
    IMPORT_ATHLETES_FAILED,

    ATTEMPT_REMOVE_ATHLETE,
    REMOVE_ATHLETE_SUCCEEDED,

    SAVE_SELECTED_ATHLETES_CATEGORIES_ATTEMPT,
    SAVE_SELECTED_ATHLETES_CATEGORIES_SUCCEEDED,
    SAVE_SELECTED_ATHLETES_CATEGORIES_FAILED,

    DELETE_ROSTER_SELECTED_ATHLETES_ATTEMPT,
    DELETE_ROSTER_SELECTED_ATHLETES_SUCCEEDED,
    DELETE_ROSTER_SELECTED_ATHLETES_FAILED,

    ATTEMPT_CANCEL_ATHLETE_INVITE,
    CANCEL_ATHLETE_INVITE_SUCCEEDED,
} from 'shared_redux/actions';

import orm from 'shared_redux/reducers/models/orm';
import API from 'services/athletes';
import { handleError, displaySuccessAlert } from 'services/alerts';
import * as AccountsSelectors from 'shared_redux/selectors/models/accounts';
import * as ProfileSelectors from 'shared_redux/selectors/view/profile';
import * as RosterSelectors from 'shared_redux/selectors/view/roster';
import * as OrganizationSwitcherSelectors from 'shared_redux/selectors/view/organization_switcher';
import history from 'shared_redux/history';

export default function* AthletesSaga() {
    yield all([
        takeLatest(FILTER_ATHLETES, filterAthletes),
        takeLatest(ATTEMPT_FETCH_ATHLETES, fetchAthletes),
        takeLatest(ATTEMPT_FETCH_ATHLETE, fetchAthlete),
        takeLatest(EDIT_ATHLETE, fetchCachedAthlete),
        takeEvery(ATTEMPT_CREATE_ATHLETE, createAthlete),
        takeEvery(ATTEMPT_SAVE_ATHLETE_METADATA, patchAthlete),
        takeEvery(UPDATE_ATHLETE_CATEGORY_ATTEMPT, patchAthleteCategory),
        takeEvery(ATTEMPT_UPLOAD_AVATAR, uploadAvatar),
        takeEvery(ATTEMPT_IMPORT_ATHLETES, importAthletes),
        takeEvery(ATTEMPT_REMOVE_ATHLETE, removeAthlete),
        takeLatest(IMPORT_ATHLETES_SUCCEEDED, refetchAthletes),

        // bulk
        takeEvery(SAVE_SELECTED_ATHLETES_CATEGORIES_ATTEMPT, patchMultipleAthletes),
        takeEvery(DELETE_ROSTER_SELECTED_ATHLETES_ATTEMPT, removeMultipleAthletes),

        takeLatest(ATTEMPT_CANCEL_ATHLETE_INVITE, cancelAthleteInvite),
    ]);
};

function *filterAthletes(action) {
    var params = new URLSearchParams(history.location.search);
    if (!action.name) {
        params.delete('name');
    } else {
        params.set('name', action.name);
    }
    history.replace(`${history.location.pathname}?${params}`);
}

function *fetchAthletes(action) {
    try {
        if (action.groupId) {
            var athletes = yield apply(API, API.getGroupAthletes, [action.groupId]);
        } else if (action.teamId) {
            var athletes = yield apply(API, API.getTeamAthletes, [action.teamId]);
        } else {
            // Use the effective organization ID from the organization switcher state
            // This ensures we use the current selected organization, not the stale account data
            let organizationId = yield select(OrganizationSwitcherSelectors.getEffectiveOrganizationId);
            
            if (!organizationId) {
                // If no organization ID is available, wait for account to be fetched
                let account = yield select(AccountsSelectors.getAccount);
                if (!account) {
                    yield take(FETCH_ACCOUNT_SUCCEEDED);
                    account = yield select(AccountsSelectors.getAccount);
                }
                organizationId = account.organization_id;
            }
            
            var athletes = yield apply(API, API.getAllAthletes, [organizationId]);
        }
        yield put({
            type: FETCH_ATHLETES_SUCCEEDED,
            payload: athletes,
        });
    } catch(error) {
        yield handleError(error, 'Error: Unable to fetch athletes');
    }
}

function *fetchAthlete(action) {
    try {
        const athlete = yield apply(API, API.getAthlete, [action.id]);
        yield put({
            type: FETCH_ATHLETE_SUCCEEDED,
            payload: athlete,
        });
    } catch(error) {
        yield handleError(error, 'Error: Unable to fetch athlete');
    }
}

function *fetchCachedAthlete(action) {
    try {
        // Fetch the athlete with account and invite information
        const athlete = yield apply(API, API.getAthlete, [action.athleteId]);
        console.log('Fetched athlete data:', {
            id: athlete.id,
            name: `${athlete.first_name} ${athlete.last_name}`,
            account_id: athlete.account_id,
            account_invite_id: athlete.account_invite_id,
            has_account_invite: !!athlete.account_invite
        });
        
        // The athlete response should now include the account_invite data
        // No need to make a second API call to fetch invitation details
        
        yield put({ type: LOADED_CACHED_ATHLETE, athlete });
    } catch (error) {
        // Fall back to the cached version if API fails
        const database = yield select(state => state.database);
        const { Athletes } = orm.mutableSession(database);
        const cachedAthlete = Athletes.withId(action.athleteId);

        if (cachedAthlete) {
            yield put({ type: LOADED_CACHED_ATHLETE, athlete: cachedAthlete });
        } else {
            yield handleError(error, 'Error: Unable to fetch athlete details');
        }
    }
}

function *createAthlete(action) {
    try {
        const json = yield select(ProfileSelectors.getAthleteJSON);
        let athlete = yield apply(API, API.createAthlete, [json]);
        if (action.file) {
            const payload = yield apply(API, API.uploadAvatar, [athlete.id]);

            // upload to s3
            yield fetch(payload.signed_url, {
                method: 'PUT',
                body: action.file,
            });

            // set athlete
            athlete = payload.athlete;
        }
        
        // Display success message with athlete name
        const athleteName = `${athlete.first_name || ''} ${athlete.last_name || ''}`.trim();
        yield call(displaySuccessAlert, `Athlete "${athleteName}" created successfully`);
        
        yield put({
            type: CREATE_ATHLETE_SUCCEEDED,
            payload: athlete,
        });
    } catch(error) {
        yield put({ type: CREATE_ATHLETE_FAILED, error });
        yield handleError(error, 'Error: Unable to create athlete');
    }
}

function *patchAthlete(action) {
    try {
        const json = yield select(ProfileSelectors.getAthleteJSON);
        const athleteId = json.id;
        console.log('Patching athlete with JSON:', json);
        console.log('Profile Redux state:', yield select(state => state.profile));

        // Basic athlete update
        const updateResult = yield apply(API, API.patchAthlete, [json]);
        console.log('Athlete updated successfully:', updateResult);
        console.log('Updated athlete team_id:', updateResult.team_id, 'group_id:', updateResult.group_id);
        
        // Handle email invitation if provided and not already accepted
        const email = yield select(ProfileSelectors.getEmail);
        const inviteStatus = yield select(ProfileSelectors.getInviteStatus);
        console.log('Email invitation check:', { email, inviteStatus });
        
        let shouldRefetch = false;
        let invitationSent = false;
        
        if (email && inviteStatus !== 'accepted') {
            try {
                console.log('Creating athlete invite for:', athleteId, email);
                const inviteResult = yield apply(API, API.createAthleteInvite, [athleteId, email]);
                console.log('Invitation created successfully:', inviteResult);
                shouldRefetch = true;
                invitationSent = true;
            } catch (error) {
                console.error('Failed to create athlete invitation:', error);
                // Don't fail the entire operation if just the invite fails
            }
        }
        
        // Update success state
        yield put({
            type: SAVE_ATHLETE_METADATA_SUCCEEDED,
            payload: updateResult
        });
        
        // Explicitly add/update the athlete in the orm model
        // This ensures the displayed data is updated right away
        yield put({
            type: FETCH_ATHLETE_SUCCEEDED,
            payload: updateResult
        });
        
        // Optionally refetch the athlete to get updated invite status
        if (shouldRefetch) {
            try {
                console.log('Refetching athlete after invite creation');
                const refreshedAthlete = yield apply(API, API.getAthlete, [athleteId]);
                yield put({ 
                    type: LOADED_CACHED_ATHLETE, 
                    athlete: refreshedAthlete 
                });
            } catch (refreshError) {
                console.error('Failed to refresh athlete data:', refreshError);
            }
        }

        // Show success message for invitation
        if (invitationSent) {
            yield call(displaySuccessAlert, `Invitation sent to ${email}`);
        }
        
        // Add a small delay before triggering full athletes list refresh
        // This ensures any other state updates are processed first
        yield delay(250);
        
        // Refresh the athletes list to update the UI
        // Get current filter parameters from URL
        const params = new URLSearchParams(window.location.search);
        const groupId = params.get('group_id');
        const teamId = params.get('team_id');
        const name = params.get('name');
        
        // Trigger a refresh of the athletes list
        yield put({
            type: ATTEMPT_FETCH_ATHLETES,
            groupId,
            teamId,
            search: name
        });
    } catch(error) {
        console.error('Error in patchAthlete:', error);
        yield handleError(error, 'Error: Unable to update athlete');
    }
}

function *patchAthleteCategory(action) {
    try {
        console.log('patchAthleteCategory called with:', action);
        
        // Check if this is a team-only selection
        let teamId = null;
        let groupId = action.groupId;
        
        if (typeof groupId === 'string' && groupId.startsWith('team:')) {
            // Extract team ID from the format "team:TEAM_ID"
            teamId = groupId.split(':')[1];
            groupId = null;
            console.log('Team-only selection detected. Team ID:', teamId, 'Group ID:', groupId);
        }
        
        // Only send group_id to the server, as groups already have a relationship to teams
        // For team-only selections, we're sending group_id: null
        const json = { 
            id: action.athleteId, 
            group_id: groupId,
            team_id: teamId // Include team_id for team-only selections
        };
        
        console.log('Sending athlete update payload:', json);
        const athlete = yield apply(API, API.patchAthlete, [json]);
        console.log('Athlete category updated successfully:', athlete);
        
        yield put({
            type: UPDATE_ATHLETE_CATEGORY_SUCCEEDED,
            payload: athlete,
        });
    } catch(error) {
        console.error('Error updating athlete category:', error);
        yield handleError(error, 'Error: Failed to change athlete team and group');
    }
}

function *uploadAvatar(action) {
    try {
        // get payload, which contains signed_url, and athlete
        const payload = yield apply(API, API.uploadAvatar, [action.athleteId]);

        // upload to s3
        yield fetch(payload.signed_url, {
            method: 'PUT',
            body: action.file,
        });

        // force refresh of image
        payload.athlete.avatar_url = `${payload.athlete.avatar_url}?${new Date().getTime()}`;

        // success
        yield put({
            type: UPLOAD_AVATAR_SUCCEEDED,
            payload: payload.athlete,
        });
    } catch(error) {
        yield handleError(error, 'Error: Unable to upload avatar');
        yield put({ type: UPLOAD_AVATAR_FAILED, error });
    }
}

function *importAthletes(action) {
    try {
        const payload = yield apply(API, API.importAthletes, [action.csv]);
        yield put({
            type: IMPORT_ATHLETES_SUCCEEDED,
            payload,
        });
    } catch(error) {
        yield handleError(error, 'Error: Unable to import athletes');
        if (error && error.statusCode && error.statusCode === 400) {
            if (error.json) {
                console.log(`ERROR JSON IS ${JSON.stringify(error.json)}`);
                if (error.json.row) {
                    yield put({ type: IMPORT_ATHLETES_FAILED, row: error.json.row, reason: error.json.reason });
                } else if (error.json.length > 0) {
                    yield put({ type: IMPORT_ATHLETES_MISSING_COLUMNS, columns: error.json });
                }
            }
        } else {
            yield put({ type: IMPORT_ATHLETES_FAILED });
        }
    }
}

function *refetchAthletes() {
    // HACK: refetch athletes
    // hack as the import command only returns the aggregate success data, not the actual individual pieces of data
    // can technically make it do so
    var params = new URLSearchParams(history.location.search);
    const groupId = params.get('group_id');
    const teamId = params.get('team_id');
    yield put({
        type: ATTEMPT_FETCH_ATHLETES,
        groupId,
        teamId,
    });
}

function *removeAthlete(action) {
    try {
        const id = yield select(ProfileSelectors.getAthleteId);
        yield apply(API, API.removeAthlete, [id]);
        yield put({
            type: REMOVE_ATHLETE_SUCCEEDED,
            id,
        });
        yield call(history.push, `/athletes`);
    } catch(error) {
        yield handleError(error, 'Error: Unable to remove athlete');
    }
}

function *patchMultipleAthletes(action) {
    try {
        const ids = yield select(RosterSelectors.getSelectedAthleteIds);
        const groupId = yield select(RosterSelectors.getSelectedGroupId);
        
        // Only send athlete_ids and group_id to the server
        const payload = yield apply(API, API.patchMultipleAthleteCategories, [ids, groupId]);
        yield put({
            type: SAVE_SELECTED_ATHLETES_CATEGORIES_SUCCEEDED,
            payload,
        });
    } catch (err) {
        yield handleError(err, 'Error: Unable to move multiple athletes');
        yield put({ type: SAVE_SELECTED_ATHLETES_CATEGORIES_FAILED });
    }
}

function *removeMultipleAthletes(action) {
    try {
        const ids = yield select(RosterSelectors.getSelectedAthleteIds);
        const payload = yield apply(API, API.removeMultipleAthletes, [ids]);
        yield put({
            type: DELETE_ROSTER_SELECTED_ATHLETES_SUCCEEDED,
            athlete_ids: ids,
            payload,
        });
    } catch (err) {
        yield handleError(err, 'Error: Unable to delete multiple athletes');
        yield put({ type: DELETE_ROSTER_SELECTED_ATHLETES_FAILED });
    }
}

function* cancelAthleteInvite() {
    try {
        const athleteId = yield select(ProfileSelectors.getAthleteId);
        
        // Call the API to cancel the athlete invite
        yield apply(API, API.cancelAthleteInvite, [athleteId]);
        
        // Update the state by refetching the athlete data
        const refreshedAthlete = yield apply(API, API.getAthlete, [athleteId]);
        yield put({ 
            type: LOADED_CACHED_ATHLETE, 
            athlete: refreshedAthlete 
        });
        
        // Dispatch success action
        yield put({
            type: CANCEL_ATHLETE_INVITE_SUCCEEDED
        });
        
        // Show success message with the correct function
        yield call(displaySuccessAlert, 'Athlete invitation cancelled successfully');
    } catch (error) {
        console.error('Error in cancelAthleteInvite:', error);
        yield handleError(error, 'Error: Unable to cancel athlete invitation');
    }
}
